<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { api, ApiError } from "@/api/client";
import type {
  NxEndpointView as NxEndpoint,
  NxProfile,
  NxProxy,
  NxThread,
  NxThreadMessage,
} from "@/api/types";

const { t } = useI18n();
const route = useRoute();

// The route id may be the literal "local" (legacy nav shortcut) or any
// value that isn't a real endpoint UUID. We resolve it against the actual
// nx-endpoints list: exact match → use it; otherwise a single endpoint →
// use that; zero or many → show a picker instead of a dead 404 page.
const routeId = computed(() => route.params.endpointId as string);
const tab = ref<"profiles" | "proxy" | "threads">("profiles");

const endpoints = ref<NxEndpoint[]>([]);
const resolvedId = ref<string | null>(null);
const pickerMode = ref(false);
const endpoint = ref<NxEndpoint | null>(null);
const profiles = ref<NxProfile[]>([]);
const proxies = ref<NxProxy[]>([]);
const threads = ref<NxThread[]>([]);
const selectedThread = ref<string | null>(null);
const messages = ref<NxThreadMessage[]>([]);
const activeProfile = ref<string>("");

const loading = ref(false);
const error = ref<string | null>(null);

async function resolveEndpoint() {
  try {
    const all = await api.nxEndpoints.list();
    endpoints.value = all.nx_endpoints;
  } catch (e) {
    error.value = describe(e);
    return;
  }
  const match = endpoints.value.find((n) => n.id === routeId.value);
  if (match) {
    resolvedId.value = match.id;
    endpoint.value = match;
  } else if (endpoints.value.length === 1) {
    resolvedId.value = endpoints.value[0].id;
    endpoint.value = endpoints.value[0];
  } else {
    pickerMode.value = true;
  }
}

async function loadProfiles() {
  if (!resolvedId.value) return;
  loading.value = true;
  error.value = null;
  try {
    const resp = await api.nx.profileList(resolvedId.value);
    profiles.value = resp.profiles;
    if (!activeProfile.value && profiles.value.length > 0) {
      activeProfile.value = profiles.value[0].name;
    }
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}

async function loadProxies() {
  if (!resolvedId.value) return;
  loading.value = true;
  error.value = null;
  try {
    const resp = await api.nx.proxyList(resolvedId.value);
    proxies.value = resp.proxies;
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}

async function loadThreads() {
  if (!resolvedId.value || !activeProfile.value) return;
  loading.value = true;
  error.value = null;
  try {
    const resp = await api.nx.threadList(resolvedId.value, activeProfile.value);
    threads.value = resp.threads;
    if (threads.value.length > 0) {
      await openThread(threads.value[0].id);
    } else {
      selectedThread.value = null;
      messages.value = [];
    }
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}

async function openThread(threadId: string) {
  if (!resolvedId.value) return;
  selectedThread.value = threadId;
  loading.value = true;
  error.value = null;
  try {
    const resp = await api.nx.threadMessages(resolvedId.value, threadId, activeProfile.value);
    messages.value = resp.messages;
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}

function describe(e: unknown): string {
  if (e instanceof ApiError) return `${e.code} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return "unknown error";
}

onMounted(async () => {
  await resolveEndpoint();
  await loadProfiles();
});

watch(tab, async (value) => {
  if (value === "profiles") return loadProfiles();
  if (value === "proxy") return loadProxies();
  if (value === "threads") return loadThreads();
});

watch(activeProfile, () => {
  if (tab.value === "threads") void loadThreads();
});
</script>

<template>
  <div class="nx">
    <header class="nx__header">
      <h1>
        {{ pickerMode ? t("nx.title") : endpoint?.display_name || endpoint?.hostname || routeId }}
      </h1>
      <span
        v-if="endpoint"
        class="admin-badge"
        :data-tone="endpoint.presence === 'online' ? 'ok' : 'bad'"
      >
        {{ endpoint.presence === "online" ? t("home.online") : t("home.offline") }}
      </span>
    </header>

    <!-- No specific endpoint resolved (e.g. /nx/local or 0/many endpoints):
         show a picker instead of a dead 404 page. -->
    <template v-if="pickerMode">
      <p v-if="endpoints.length === 0" class="admin-empty">{{ t("nx.none") }}</p>
      <ul v-else class="nx__picker">
        <li v-for="n in endpoints" :key="n.id">
          <RouterLink class="nx__picker-item" :to="{ name: 'nx', params: { endpointId: n.id } }">
            <strong>{{ n.display_name || n.hostname }}</strong>
            <span class="admin-badge" :data-tone="n.presence === 'online' ? 'ok' : 'bad'">{{
              n.presence === "online" ? t("home.online") : t("home.offline")
            }}</span>
          </RouterLink>
        </li>
      </ul>
    </template>

    <nav v-if="!pickerMode" class="nx__tabs" role="tablist">
      <button
        type="button"
        :class="['nx__tab', { 'nx__tab--active': tab === 'profiles' }]"
        @click="tab = 'profiles'"
      >
        {{ t("nx.tab_profiles") }}
      </button>
      <button
        type="button"
        :class="['nx__tab', { 'nx__tab--active': tab === 'proxy' }]"
        @click="tab = 'proxy'"
      >
        {{ t("nx.tab_proxy") }}
      </button>
      <button
        type="button"
        :class="['nx__tab', { 'nx__tab--active': tab === 'threads' }]"
        @click="tab = 'threads'"
      >
        {{ t("nx.tab_threads") }}
      </button>
    </nav>

    <p v-if="error" class="admin-error">{{ error }}</p>

    <section v-if="!pickerMode && tab === 'profiles'" class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="profiles.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("nx.profile_tool") }}</th>
            <th>{{ t("nx.profile_name") }}</th>
            <th>{{ t("nx.profile_home") }}</th>
            <th>{{ t("nx.profile_last_proxy") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in profiles" :key="`${p.tool}/${p.name}`">
            <td>{{ p.tool }}</td>
            <td>{{ p.name }}</td>
            <td class="admin-table__id">{{ p.home }}</td>
            <td>{{ p.last_proxy ?? "—" }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="!pickerMode && tab === 'proxy'" class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="proxies.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("nx.proxy_name") }}</th>
            <th>{{ t("nx.proxy_selector") }}</th>
            <th>{{ t("nx.proxy_aliases") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="px in proxies" :key="px.name">
            <td>{{ px.name }}</td>
            <td class="admin-table__id">{{ px.selector }}</td>
            <td>{{ px.aliases?.join(", ") ?? "—" }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="!pickerMode && tab === 'threads'" class="admin-card nx__threads">
      <div class="nx__threads-controls">
        <label>
          <span>{{ t("nx.profile") }}</span>
          <select v-model="activeProfile">
            <option v-for="p in profiles" :key="`${p.tool}/${p.name}`" :value="p.name">
              {{ p.tool }}/{{ p.name }}
            </option>
          </select>
        </label>
        <button type="button" @click="loadThreads">{{ t("nx.threads_refresh") }}</button>
      </div>
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="threads.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <div v-else class="nx__threads-pane">
        <ul class="nx__threads-list">
          <li
            v-for="th in threads"
            :key="th.id"
            :class="{ 'nx__threads-active': selectedThread === th.id }"
          >
            <button type="button" @click="openThread(th.id)">
              <strong>{{ th.title }}</strong>
              <span class="nx__threads-preview">{{ th.preview ?? "" }}</span>
            </button>
          </li>
        </ul>
        <ol v-if="selectedThread" class="nx__messages">
          <li v-for="(m, i) in messages" :key="i" :data-role="m.role">
            <span class="nx__messages-role">{{ m.role }}</span>
            <pre class="nx__messages-content">{{ m.content }}</pre>
          </li>
        </ol>
      </div>
    </section>
  </div>
</template>

<style scoped>
.nx {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 64rem;
}
.nx__header {
  display: flex;
  align-items: baseline;
  gap: 0.85rem;
}
.nx__header h1 {
  margin: 0;
  font-size: 1.1rem;
}
.nx__tabs {
  display: flex;
  gap: 0.4rem;
  border-bottom: 1px solid var(--color-border);
}
.nx__tab {
  background: transparent;
  border: none;
  color: var(--color-fg-dim);
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.nx__tab--active {
  color: var(--color-fg);
  border-bottom-color: var(--color-accent);
  font-weight: 600;
}
.nx__threads-controls {
  display: flex;
  gap: 0.6rem;
  align-items: end;
  margin-bottom: 0.8rem;
}
.nx__threads-controls label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
}
.nx__threads-controls select {
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
}
.nx__threads-pane {
  display: grid;
  grid-template-columns: 18rem 1fr;
  gap: 1rem;
}
.nx__threads-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.nx__threads-list button {
  width: 100%;
  text-align: left;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.5rem 0.7rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  color: var(--color-fg);
}
.nx__threads-active button {
  border-color: var(--color-accent);
}
.nx__threads-preview {
  font-size: 0.78rem;
  color: var(--color-fg-dim);
}
.nx__messages {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.nx__messages li {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-elev);
}
.nx__messages-role {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-fg-dim);
}
.nx__messages-content {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.85rem;
  white-space: pre-wrap;
}
.nx__picker {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.nx__picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-elev);
  color: var(--color-fg);
  min-height: 44px;
}
</style>
